import { api } from '../api.ts';
import { ft, szam } from '../formazas.ts';
import { ablakotBezar, ablakotNyit, dokumentumMegjelenites, esc } from './dokumentum-ablak.ts';

/**
 * A havi zárás nyomtatható kimutatása. (a megrendelő kérése, 2026-09-28)
 *
 * A **napi zárás rövid kimutatását** veszi alapul: szellős elrendezés,
 * nagy számok, egyetlen A4-es oldal. Ott a nap végi leadó a főszám; itt a
 * hónap eladott mennyisége és értéke — a többi szám csoportonként,
 * mellettük az előző hónap és a változás.
 *
 * A mennyiség ugyanaz, amit a Napi bontás mutat: a lezárt napok és a
 * forgalmi előzmény együtt; az érték és az áfa csak a lezárt napokból.
 *
 * A böngésző nyomtatásból PDF-be menti, ahogy a napi kimutatást is.
 */

interface ZarasSzam {
  cimke: string;
  ertek: number;
  egyseg: string;
  elozo: number;
  valtozas: number | null;
  megjegyzes?: string;
}

interface ZarasCsoport {
  cim: string;
  szamok: ZarasSzam[];
}

interface Zaras {
  honap: string;
  elozo_honap: string;
  lezart_napok: number;
  elozo_lezart_napok: number;
  van_adat: boolean;
  csoportok: ZarasCsoport[];
}

const CSS = `
  .lap.zaras { font: 13px/1.4 "Segoe UI", system-ui, -apple-system, sans-serif; padding: 16mm 16mm; }
  .zaras .zfej { display: flex; justify-content: space-between; align-items: baseline;
                 border-bottom: 2.5px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
  .zaras .zfej b { font-size: 22px; }
  .zaras .zfej .honap { font-size: 20px; font-weight: 700; }
  .zaras .zfej .all { display: block; text-align: right; font-size: 11px; text-transform: uppercase;
                      letter-spacing: .08em; color: #555; margin-top: 2px; }
  /* A két főszám kerettel, ahogy a napi kimutatáson a leadó. */
  .zaras .fo { display: grid; grid-template-columns: 1fr 1fr; gap: 0;
               border: 2.5px solid #000; margin-bottom: 18px; }
  .zaras .fo > div { padding: 14px 18px; }
  .zaras .fo > div + div { border-left: 1px solid #000; }
  .zaras .fo em { display: block; font-style: normal; font-size: 12px; font-weight: 800;
                  letter-spacing: .1em; text-transform: uppercase; }
  .zaras .fo span { font-family: ui-monospace, Menlo, Consolas, monospace; font-variant-numeric: tabular-nums;
                    font-size: 32px; font-weight: 800; white-space: nowrap; }
  .zaras .fo span i { font-style: normal; font-size: 16px; font-weight: 400; margin-left: 5px; }
  .zaras .fo small { display: block; font-size: 11px; color: #555; margin-top: 3px; }
  .zaras .racs { display: grid; grid-template-columns: 1fr 1fr; gap: 0 26px; }
  .zaras h3 { font-size: 11px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
              margin: 14px 0 3px; padding-bottom: 2px; border-bottom: 1px solid #000; }
  .zaras .racs > div > h3:first-child { margin-top: 0; }
  .zaras table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .zaras td { padding: 4px 0; border-bottom: 1px solid #eee; vertical-align: baseline; }
  .zaras td.r { text-align: right; font-family: ui-monospace, Menlo, Consolas, monospace;
                font-variant-numeric: tabular-nums; white-space: nowrap; font-weight: 700; }
  .zaras td.r i { font-style: normal; font-weight: 400; color: #777; font-size: 10.5px; margin-left: 3px; }
  .zaras td.v { text-align: right; white-space: nowrap; font-size: 11px; width: 74px; }
  .zaras td small { display: block; font-size: 10.5px; color: #666; }
  .zaras .fel { color: #1a7a56; font-weight: 700; }
  .zaras .le { color: #a8281f; font-weight: 700; }
  .zaras .nincs { color: #999; }
  .zaras .elab { margin-top: 20px; font-size: 10px; color: #777; border-top: 1px solid #ddd; padding-top: 6px; }
  @media print { .lap.zaras { padding: 0; } }
`;

/** A számot a mértékegysége szerint formázzuk — az átlagár tizedessel. */
function ertekSzoveg(sz: ZarasSzam, ertek = sz.ertek): string {
  return sz.egyseg === 'Ft/liter' ? szam(ertek, 1) : ft(ertek);
}

function valtozasSzoveg(sz: ZarasSzam): string {
  if (sz.valtozas === null) return `<span class="nincs">—</span>`;
  if (sz.valtozas === 0) return `<span class="nincs">változatlan</span>`;
  const fel = sz.valtozas > 0;
  return `<span class="${fel ? 'fel' : 'le'}">${fel ? '▲' : '▼'} ${szam(Math.abs(sz.valtozas), 1)}%</span>`;
}

function csoportTabla(cs: ZarasCsoport): string {
  return (
    `<h3>${esc(cs.cim)}</h3><table>` +
    cs.szamok
      .map(
        (sz) =>
          `<tr><td>${esc(sz.cimke)}${sz.megjegyzes ? `<small>${esc(sz.megjegyzes)}</small>` : ''}</td>` +
          `<td class="r">${ertekSzoveg(sz)}<i>${esc(sz.egyseg)}</i></td>` +
          `<td class="v">${valtozasSzoveg(sz)}</td></tr>`,
      )
      .join('') +
    `</table>`
  );
}

function lap(z: Zaras, keszult: string): string {
  const eladas = z.csoportok.find((cs) => cs.cim === 'Eladás')!;
  const liter = eladas.szamok.find((s) => s.cimke === 'Eladott mennyiség')!;
  const ertek = eladas.szamok.find((s) => s.cimke === 'Eladás értéke')!;
  const elozmeny = eladas.szamok.find((s) => s.cimke === 'Ebből forgalmi előzmény');

  /* A két hasáb: a csoportok felváltva, hogy körülbelül egyenlő magas legyen. */
  const bal = z.csoportok.filter((_, i) => i % 2 === 0);
  const jobb = z.csoportok.filter((_, i) => i % 2 === 1);

  return (
    `<div class="lap zaras">` +
    `<div class="zfej"><b>Havi zárás</b><div><span class="honap">${esc(z.honap)}</span>` +
    `<span class="all">${z.lezart_napok} lezárt nap · összevetve: ${esc(z.elozo_honap)}` +
    ` (${z.elozo_lezart_napok} nap)</span></div></div>` +
    `<div class="fo">` +
    `<div><em>Eladott mennyiség</em><span>${ft(liter.ertek)}<i>liter</i></span>` +
    (elozmeny && elozmeny.ertek > 0
      ? `<small>ebből forgalmi előzmény: ${ft(elozmeny.ertek)} liter</small>`
      : '') +
    `<small>előző hónap: ${ft(liter.elozo)} liter · ${
      liter.valtozas === null ? 'nincs mihez mérni' : `${liter.valtozas > 0 ? '+' : ''}${szam(liter.valtozas, 1)}%`
    }</small></div>` +
    `<div><em>Eladás értéke</em><span>${ft(ertek.ertek)}<i>Ft</i></span>` +
    `<small>előző hónap: ${ft(ertek.elozo)} Ft · ${
      ertek.valtozas === null ? 'nincs mihez mérni' : `${ertek.valtozas > 0 ? '+' : ''}${szam(ertek.valtozas, 1)}%`
    }</small></div>` +
    `</div>` +
    `<div class="racs"><div>${bal.map(csoportTabla).join('')}</div>` +
    `<div>${jobb.map(csoportTabla).join('')}</div></div>` +
    `<div class="elab">Készült: ${esc(keszult)} · havi zárás · ${esc(z.honap)}` +
    ` · a mennyiség a lezárt napokból és a forgalmi előzményből, az érték <b>csak a lezárt napokból</b>` +
    ` · a százalék az előző hónaphoz (${esc(z.elozo_honap)}) mérve</div>` +
    `</div>`
  );
}

/** A havi zárás megnyitása nyomtatható lapként. */
export async function zarasMentese(honap: string): Promise<void> {
  // Az ablak még a kattintásban nyílik — a böngésző csak így engedi.
  const w = ablakotNyit(CSS);
  try {
    const z = await api.get<Zaras>(`/api/kimutatas/zaras/${honap}`);
    if (!z.van_adat) {
      ablakotBezar(w);
      throw new Error('Ebben a hónapban még nincs lezárt nap, ezért nincs mit kimutatni.');
    }
    const keszult = new Date().toLocaleString('hu-HU');
    dokumentumMegjelenites(w, `Havi zárás — ${honap}`, lap(z, keszult), CSS);
  } catch (h) {
    ablakotBezar(w);
    throw h;
  }
}
